/**
 * Root Angular module wiring core dependencies, MSAL auth, Kendo UI, and
 * the generated ResourceWerx API module. Centralizes interceptors and global providers.
 */
import {APP_BASE_HREF} from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import {NbDialogModule, NbLayoutModule, NbThemeModule} from '@nebular/theme';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {DateInputsModule} from '@progress/kendo-angular-dateinputs';
import {DialogsModule} from '@progress/kendo-angular-dialog';
import {DropDownsModule} from '@progress/kendo-angular-dropdowns';
import {GridModule} from '@progress/kendo-angular-grid';
import {TreeListModule} from '@progress/kendo-angular-treelist';
import {InputsModule} from '@progress/kendo-angular-inputs';
import {CldrIntlService, IntlService} from '@progress/kendo-angular-intl';
import '@progress/kendo-angular-intl/locales/en-CA/all';
import {LayoutModule} from '@progress/kendo-angular-layout';
import {SchedulerModule} from '@progress/kendo-angular-scheduler';
import {TooltipModule} from '@progress/kendo-angular-tooltip';
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
import {environment} from '../environments/environment';
import {ResourceWerxApiModule, BASE_PATH, ResourceWerxConfiguration, ResourceWerxConfigurationParameters} from '../resourcewerx-api';
import {CoreModule} from './@core/core.module';
import {GlobalErrorHandlerService} from './@core/utils/global-error-handler.service';
import {GlobalHttpErrorInterceptorService} from './@core/utils/global-http-error-interceptor.service';
import {BlobHttpErrorInterceptorService} from './@core/utils/blob-http-error-interceptor.service';
import {ThemeModule} from './@theme/theme.module';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent, KENDO_ANGULAR_GRID_CONFIG} from './app.component';
import {MsalCustomInterceptor} from './pages/auth/msal-custom-interceptor';
import {ErrorService} from './pages/shared/errors/error.service';
import {kendoGridConfigFactory, SharedModule} from './pages/shared/shared.module';
import {KendoAngularGridModule} from './@core/compat/framewerx-kendo-angular-grid.module';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication, LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {NgIdleModule} from '@ng-idle/core';
import 'hammerjs';
import {GanttModule} from '@progress/kendo-angular-gantt';
import {GlobalHttpBatchErrorInterceptorService} from './@core/utils/global-http-batch-error-interceptor.service';
import {ObjectByStringPipe} from '@framewerx/common';

/**
 * Configures the generated ResourceWerx API client with base URL and credentials.
 */
export function apiConfigFactory(): ResourceWerxConfiguration {
  const params: ResourceWerxConfigurationParameters = {
    // set configuration parameters here.
    credentials: {Authorization: null},
    basePath: environment.apiBaseUrl,
  };
  return new ResourceWerxConfiguration(params);
}

/**
 * Maps MSAL browser log levels to NGXLogger outputs.
 */
export function loggerCallback(logger: NGXLogger, logLevel: LogLevel, message: string, containsPii: boolean) {
  switch (logLevel) {
    case LogLevel.Error:
      logger.error(message);
      break;
    case LogLevel.Info:
      logger.info(message);
      break;
    case LogLevel.Verbose:
      logger.trace(message);
      break;
    case LogLevel.Warning:
      logger.warn(message);
      break;
    default:
      logger.log(message);
      break;
  }
}

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

/**
 * Creates the MSAL public client application with environment-driven config.
 */
export function MSALInstanceFactory(logger: NGXLogger): IPublicClientApplication {
  const logLevel = environment.msalLoggingLevel || LogLevel.Info;
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureClientId,
      authority: environment.authority,
      redirectUri: `${window.location.origin}/auth`,
      postLogoutRedirectUri: `${window.location.origin}/pages/dashboard`,
      navigateToLoginRequestUrl: true, // default=true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage, // localStorage works across tabs but is less secure,
      storeAuthStateInCookie: isIE, // set to true for IE11
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          loggerCallback(logger, level, message, containsPii);
        },
        logLevel: logLevel, // LogLevel.Verbose for debugging
        piiLoggingEnabled: false, // true for debugging
      },
    },
  });
}

/**
 * Configures MSAL HTTP interceptor with protected resource map.
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>(<[string, string[]][]>environment.protectedResourceMap);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Configures MSAL route guard interaction and failure route.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    // authRequest: {
    //   scopes: ['user.read'],
    // },
    loginFailedRoute: '/pages/auth/failed',
  };
}


@NgModule({ declarations: [
        AppComponent,
    ],
    bootstrap: [AppComponent, MsalRedirectComponent],
    exports: [], imports: [
        // Levels: TRACE|DEBUG|INFO|LOG|WARN|ERROR|FATAL|OFF
        LoggerModule.forRoot({
            // serverLoggingUrl: '/api/logs', // TODO: Implement logging in server https://stackoverflow.com/a/53078568/2557554
            level: environment.loggingLevel,
            serverLogLevel: NgxLoggerLevel.ERROR,
        }),
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        NgbModule,
        ThemeModule.forRoot(),
        CoreModule.forRoot(),
        NbDialogModule.forRoot(),
        MsalModule,
        TreeListModule,
        GridModule,
        GanttModule,
        DropDownsModule,
        DialogsModule,
        LayoutModule,
        DateInputsModule,
        TooltipModule,
        SharedModule,
        SchedulerModule,
        InputsModule,
        ResourceWerxApiModule.forRoot(apiConfigFactory),
        NbThemeModule.forRoot({ name: 'corporate' }),
        NbLayoutModule,
        NgIdleModule.forRoot(),
        KendoAngularGridModule.forRoot(kendoGridConfigFactory())], providers: [
        { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: GlobalHttpErrorInterceptorService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BlobHttpErrorInterceptorService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: GlobalHttpBatchErrorInterceptorService,
            multi: true,
        },
        CldrIntlService,
        {
            provide: IntlService,
            useExisting: CldrIntlService,
        },
        {
            provide: LOCALE_ID,
            useValue: 'en-CA',
        },
        { provide: APP_BASE_HREF, useValue: '/' },
        { provide: BASE_PATH, useValue: environment.apiBaseUrl },
        // {provide: STEMS_BASE_PATH, useValue: environment.stemsBaseUrl},
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalCustomInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
            deps: [NGXLogger],
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        ErrorService,
        ObjectByStringPipe,
        { provide: KENDO_ANGULAR_GRID_CONFIG, useFactory: kendoGridConfigFactory },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
}

